import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowPathIcon, ArrowUpTrayIcon, PhotoIcon, XMarkIcon, CheckIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import {
  approveCompanyBanner,
  deleteCompanyBanner,
  listCompanyBanners,
  rejectCompanyBanner,
  uploadCompanyBanner,
} from "../api/companyBannerModerationAPI";

const TABS = [
  { key: "pending", label: "На проверке", dot: "bg-amber-500" },
  { key: "approved", label: "Одобренные", dot: "bg-emerald-500" },
  { key: "rejected", label: "Отклонённые", dot: "bg-rose-500" },
  { key: "all", label: "Все", dot: "bg-gray-400" },
];

const errText = (e, fallback) => {
  if (e?.status === 401) return "Сессия истекла. Войдите заново.";
  if (e?.status === 403) return "Недостаточно прав.";
  if (e?.status === 404) return "Endpoint не найден. Backend не задеплоен.";
  if (e?.status >= 500) return "Ошибка сервера. Повторите позже.";
  return e?.message || fallback;
};

const statusBadge = (u) => {
  if (u.banner_status === "pending" || u.banner_pending) return { label: "На проверке", cls: "bg-amber-500" };
  if (u.banner_status === "rejected") return { label: "Отклонён", cls: "bg-rose-500" };
  if (u.banner) return { label: "Одобрен", cls: "bg-emerald-600" };
  return { label: "Без баннера", cls: "bg-gray-500" };
};

const btn = "rounded-lg px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50";

function BannerCard({ user, onApprove, onReject, onDelete, onUpload, busy }) {
  const [mode, setMode] = useState(null); // 'reject' | 'delete'
  const [rejectReason, setRejectReason] = useState("");
  const fileInputRef = useRef(null);
  const badge = statusBadge(user);
  const previewUrl = user.banner_pending || user.banner;
  const canModerate = !!user.banner_pending;
  const canDelete = !!(user.banner || user.banner_pending || user.banner_status);

  return (
    <div className={`flex flex-col overflow-hidden rounded-block border border-border-primary bg-bg-card shadow-sm ${busy ? "opacity-60" : ""}`}>
      <div className="relative aspect-[3/1] bg-bg-tertiary">
        {previewUrl ? (
          <a href={previewUrl} target="_blank" rel="noopener noreferrer">
            <img src={previewUrl} alt="" className="h-full w-full object-cover" />
          </a>
        ) : (
          <div className="flex h-full w-full items-center justify-center text-text-muted">
            <PhotoIcon className="h-8 w-8" />
          </div>
        )}
        <span className={`absolute left-3 top-3 rounded-full px-2.5 py-0.5 text-xs font-semibold text-white shadow ${badge.cls}`}>{badge.label}</span>
        {busy && (
          <span className="absolute inset-0 flex items-center justify-center bg-black/30">
            <ArrowPathIcon className="h-6 w-6 animate-spin text-white" />
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-center gap-3">
          {user.avatar_url ? (
            <img src={user.avatar_url} alt="" className="h-10 w-10 rounded-full object-cover" />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-grad text-sm font-semibold text-white">
              {(user.name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-text-primary">{user.name || `Пользователь #${user.user_id}`}</div>
            <div className="truncate text-xs text-text-muted">
              <span className="font-mono">#{user.user_id}</span>
              {(user.phone_number || user.email) && ` · ${user.phone_number || user.email}`}
            </div>
          </div>
        </div>

        {user.banner && user.banner_pending && user.banner !== user.banner_pending && (
          <div className="rounded-lg border border-border-primary bg-bg-primary p-2">
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">Сейчас на профиле</div>
            <img src={user.banner} alt="" className="aspect-[3/1] w-full rounded object-cover" />
          </div>
        )}

        {user.banner_rejection_reason && (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-2.5 py-1.5 text-xs text-rose-500">
            Причина отклонения: {user.banner_rejection_reason}
          </div>
        )}

        {mode === "reject" && (
          <div className="space-y-2">
            <textarea
              autoFocus
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Причина — пользователь увидит её (необязательно)"
              rows={2}
              className="w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-500/30"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  onReject(user.user_id, rejectReason);
                  setMode(null);
                  setRejectReason("");
                }}
                disabled={busy}
                className={`${btn} flex-1 bg-rose-600 text-white hover:bg-rose-700`}
              >
                Отклонить
              </button>
              <button type="button" onClick={() => setMode(null)} className={`${btn} border border-border-primary text-text-secondary hover:bg-bg-tertiary`}>
                Отмена
              </button>
            </div>
          </div>
        )}

        {mode === "delete" && (
          <div className="space-y-2 rounded-lg border border-rose-500/30 bg-rose-500/[0.07] p-2.5">
            <p className="text-sm text-text-primary">Удалить баннер с профиля (и ожидающий проверки)? Это необратимо.</p>
            <div className="flex gap-2">
              <button
                type="button"
                autoFocus
                onClick={() => {
                  onDelete(user.user_id);
                  setMode(null);
                }}
                disabled={busy}
                className={`${btn} flex-1 bg-rose-600 text-white hover:bg-rose-700`}
              >
                Да, удалить
              </button>
              <button type="button" onClick={() => setMode(null)} className={`${btn} border border-border-primary text-text-secondary hover:bg-bg-tertiary`}>
                Отмена
              </button>
            </div>
          </div>
        )}

        {!mode && (
          <div className="mt-auto flex flex-wrap gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) onUpload(user.user_id, f);
              }}
              className="hidden"
            />
            {canModerate && (
              <>
                <button type="button" onClick={() => onApprove(user.user_id)} disabled={busy} className={`${btn} inline-flex flex-1 items-center justify-center gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700`}>
                  <CheckIcon className="h-4 w-4" /> Одобрить
                </button>
                <button type="button" onClick={() => setMode("reject")} disabled={busy} className={`${btn} flex-1 border border-rose-500/40 text-rose-500 hover:bg-rose-500/10`}>
                  Отклонить
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              title="Загрузить/заменить баннер — сразу активен, без модерации"
              className={`${btn} flex-1 border border-border-primary text-text-primary hover:bg-bg-tertiary`}
            >
              {user.banner ? "Заменить" : "Загрузить"}
            </button>
            {canDelete && (
              <button type="button" onClick={() => setMode("delete")} disabled={busy} className={`${btn} border border-border-primary text-text-muted hover:border-rose-500/40 hover:text-rose-500`}>
                Удалить
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CompanyBannerModeration() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [busyUserId, setBusyUserId] = useState(null);
  const [manualUserId, setManualUserId] = useState("");
  const manualInputRef = useRef(null);

  useEffect(() => {
    if (!toast || toast.type === "error") return undefined;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listCompanyBanners(tab, 200);
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setItems([]);
      setToast({ type: "error", text: errText(e, "Не удалось загрузить список.") });
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const wrap = async (userId, fn, successMsg) => {
    setBusyUserId(userId);
    setToast(null);
    try {
      await fn();
      setToast({ type: "success", text: successMsg });
      await load();
    } catch (e) {
      setToast({ type: "error", text: errText(e, "Ошибка операции.") });
    } finally {
      setBusyUserId(null);
    }
  };

  const handleApprove = (userId) => wrap(userId, () => approveCompanyBanner(userId), "Баннер одобрен и показывается на профиле.");
  const handleReject = (userId, reason) =>
    wrap(userId, () => rejectCompanyBanner(userId, reason?.trim() || null), "Баннер отклонён. Прежний баннер (если был) остался.");
  const handleDelete = (userId) => wrap(userId, () => deleteCompanyBanner(userId, "all"), "Баннер удалён с профиля.");
  const handleUpload = (userId, file) => wrap(userId, () => uploadCompanyBanner(userId, file), "Баннер загружен и сразу активен.");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                tab === t.key ? "border-[#4F45E5]/50 bg-[#4F45E5]/15 text-text-primary" : "border-border-primary text-text-secondary hover:bg-bg-tertiary"
              }`}
            >
              <span className={`h-2 w-2 rounded-full ${t.dot}`} />
              {t.label}
              {tab === t.key && !loading && <span className="text-xs text-text-muted">{items.length}</span>}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-lg border border-border-primary bg-bg-card">
            <input
              type="text"
              inputMode="numeric"
              value={manualUserId}
              onChange={(e) => setManualUserId(e.target.value.replace(/\D/g, ""))}
              placeholder="ID пользователя"
              aria-label="ID пользователя для загрузки баннера"
              className="w-36 rounded-l-lg bg-transparent px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none"
            />
            <input
              ref={manualInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                const uid = parseInt(manualUserId, 10);
                if (!f || !uid) return;
                handleUpload(uid, f);
                setManualUserId("");
              }}
            />
            <button
              type="button"
              onClick={() => manualInputRef.current?.click()}
              disabled={!manualUserId || busyUserId != null}
              title="Загрузить баннер пользователю — активируется сразу, без модерации"
              className="inline-flex items-center gap-1.5 border-l border-border-primary px-3 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary disabled:opacity-40"
            >
              <ArrowUpTrayIcon className="h-4 w-4" /> Загрузить
            </button>
          </div>
          <button type="button" onClick={load} disabled={loading} title="Обновить" aria-label="Обновить" className="rounded-lg border border-border-primary p-2 text-text-secondary hover:bg-bg-tertiary">
            <ArrowPathIcon className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {tab === "approved" && (
        <p className="flex items-start gap-2 rounded-lg border border-border-primary bg-bg-card px-4 py-2.5 text-xs text-text-secondary">
          <InformationCircleIcon className="h-4 w-4 shrink-0 text-text-muted" />
          Одобренные баннеры показываются в профиле компании. Баннер главной страницы выбирается во вкладке «Публичный баннер».
        </p>
      )}

      {toast && (
        <div
          role={toast.type === "error" ? "alert" : "status"}
          className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${
            toast.type === "error" ? "border-rose-500/30 bg-rose-500/10 text-rose-500" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-500"
          }`}
        >
          <span>{toast.text}</span>
          <button type="button" onClick={() => setToast(null)} aria-label="Закрыть" className="shrink-0 opacity-70 hover:opacity-100">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {loading && items.length === 0 ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="animate-pulse overflow-hidden rounded-block border border-border-primary bg-bg-card">
              <div className="aspect-[3/1] bg-bg-tertiary" />
              <div className="space-y-2 p-4">
                <div className="h-3 w-1/2 rounded bg-bg-tertiary" />
                <div className="h-3 w-1/3 rounded bg-bg-tertiary" />
              </div>
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-block border border-dashed border-border-primary bg-bg-card px-6 py-12 text-center text-sm text-text-secondary">
          {tab === "pending" ? "Нет баннеров на проверке 🎉" : "В этой категории пусто."}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((u) => (
            <BannerCard
              key={u.user_id}
              user={u}
              onApprove={handleApprove}
              onReject={handleReject}
              onDelete={handleDelete}
              onUpload={handleUpload}
              busy={busyUserId === u.user_id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default CompanyBannerModeration;
